'use client';

import { cn } from '@ui/lib/utils';
import { Star, X } from 'lucide-react';
import { useState } from 'react';

export interface RatingProps {
  id?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  cancel?: boolean;
  stars?: number;
  className?: string;
}

export function Rating({
  id,
  value,
  onChange,
  disabled = false,
  cancel = true,
  stars = 5,
  className,
}: RatingProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const handleStarClick = (starValue: number) => {
    if (!disabled) {
      onChange(starValue);
    }
  };

  const handleCancelClick = () => {
    if (!disabled) {
      onChange(null);
    }
  };

  return (
    <div className={cn('flex items-center gap-1', className)} id={id}>
      {cancel && (
        <button
          onClick={handleCancelClick}
          disabled={disabled}
          className={cn(
            'text-red-500 hover:text-red-700 transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded'
          )}
          type="button"
          aria-label="Cancel rating"
        >
          <X className="h-5 w-5" />
        </button>
      )}
      {Array.from({ length: stars }, (_, i) => i + 1).map((starValue) => {
        const isActive = (hoverValue || value || 0) >= starValue;
        return (
          <button
            key={starValue}
            onClick={() => handleStarClick(starValue)}
            onMouseEnter={() => !disabled && setHoverValue(starValue)}
            onMouseLeave={() => !disabled && setHoverValue(null)}
            disabled={disabled}
            className={cn(
              'transition-colors cursor-pointer',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded'
            )}
            type="button"
            aria-label={`Rate ${starValue} out of ${stars}`}
          >
            <Star
              className={cn(
                'h-5 w-5 transition-colors',
                isActive ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
